import { useCallback, useEffect, useRef, useState, useMemo } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { fg } from '@confluence/feature-gating';

import { ExperimentalHeartbeatUserType as StartUserType } from './__types__/EditorStartHeartbeatMutation.ts';
import { ExperimentalHeartbeatUserType as StopUserType } from './__types__/EditorStopHeartbeatMutation.ts';
import type {
	EditorStartHeartbeatMutation_experimentalEditorStartHeartbeat_orderedUsers as ActiveUser,
	EditorStartHeartbeatMutation as EditorStartHeartbeatMutationResponse,
} from './__types__/EditorStartHeartbeatMutation.ts';
import { useExperimentalEditorStartHeartbeatMutation } from './useExperimentalEditorStartHeartbeatMutation.ts';
import { useExperimentalEditorStopHeartbeatMutation } from './useExperimentalEditorStopHeartbeatMutation.ts';

/* Editor heartbeat defaults to every 60 seconds. */
const DEFAULT_HEARTBEAT_TIMER_INTERVAL = 60000;
const MIN_HEARTBEAT_TIMER_INTERVAL = DEFAULT_HEARTBEAT_TIMER_INTERVAL;
/* Editor heartbeat is capped at 5 minutes. */
const MAX_HEARTBEAT_TIMER_INTERVAL = 5 * 60 * 1000;

const EDITOR_HEARTBEAT_INTERVAL_MS = 'confluence_editor_heartbeat_interval_ms';

const EDITOR_NCS_MAX_PARTICIPANTS = 'confluence_frontend_collab_ncs_max_participants';

const LIVE_PAGES_LIMITS = 'confluence_frontend_collab_live_pages_limits';

const COLLAB_SERVICE = 'ncs'; // this hook is only used for NCS editor

const DEFAULT_MAX_EDIT_PARTICIPANTS = 12;

const getHeartbeatTimerInterval = () => {
	if (!fg('confluence_editor_use_custom_heartbeat_interval')) {
		return DEFAULT_HEARTBEAT_TIMER_INTERVAL;
	}

	const configuredInterval = StatsigConfigurations.getNumber(
		EDITOR_HEARTBEAT_INTERVAL_MS,
		DEFAULT_HEARTBEAT_TIMER_INTERVAL,
	);

	if (
		!Number.isFinite(configuredInterval) ||
		configuredInterval < MIN_HEARTBEAT_TIMER_INTERVAL ||
		configuredInterval > MAX_HEARTBEAT_TIMER_INTERVAL
	) {
		return DEFAULT_HEARTBEAT_TIMER_INTERVAL;
	}

	return configuredInterval;
};

type LivePagesLimitConfig = {
	editLimit: number;
	viewLimit: number;
};

export class CollabHeartbeatError extends Error {
	constructor(
		public message: string,
		public reason: string,
	) {
		super(message);
		this.reason = reason;
	}
}

export const isPermissionError = (heartbeatError: CollabHeartbeatError | undefined): boolean => {
	return heartbeatError?.reason === 'unauthorised' || heartbeatError?.reason === 'UNAUTHORIZED';
};

export type HeartbeatData = {
	collabLimitReached: boolean;
	liveEditLimitReached: boolean;
	liveViewLimitReached: boolean;
	heartbeatError?: CollabHeartbeatError;
	stopHeartbeatCallback: () => void;
	editorSessionId: string | null;
	subtypeMismatch: boolean;
	heartbeatSubtype: string | null | undefined;
};

/**
 * Hook used in editor to send heartbeat to Confluence backend signifying an active session.
 * Upon unmount (closing the editor), the stop heartbeat request will be sent
 * Heartbeat must be sent continually or else Confluence backend with time out the activity
 */
export const useEditorHeartbeat = ({
	contentId,
	contentType,
	collabEditInitialized,
	viewMode,
}: {
	contentId: string;
	contentType: string;
	collabEditInitialized: boolean;
	viewMode?: boolean;
}): HeartbeatData => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { userId } = useSessionData();
	const heartbeatTimerId = useRef<null | ReturnType<typeof setInterval>>(null);
	const editorSessionIdRef = useRef<null | string>(null);
	const [subtypeMismatch, setSubtypeMismatch] = useState<boolean>(false);
	const [heartbeatSubtype, setHeartbeatSubtype] = useState<string | null | undefined>(undefined);

	// If view mode changes, trigger new heartbeat request with new "user type", before enforcing any limits or returning errors
	const prevViewModeRef = useRef<null | boolean>(viewMode !== undefined ? viewMode : null);
	const isResettingHeartbeat = useRef<boolean>(false);

	// keep track of list of users from heartbeat response, which are ordered by time that they joined the collaborative session
	const [orderedActiveUsers, setOrderedActiveUsers] = useState<Array<ActiveUser> | null>(null);

	const isLivePage = useIsCurrentPageLive();

	// used for regular pages
	const [collabLimitReached, setCollabLimitReached] = useState<boolean>(false);

	// used for live pages
	const [liveEditLimitReached, setLiveEditLimitReached] = useState<boolean>(false);
	const [liveViewLimitReached, setLiveViewLimitReached] = useState<boolean>(false);

	const [heartbeatError, setHeartbeatError] = useState<CollabHeartbeatError | undefined>();

	let maxEditParticipants = StatsigConfigurations.getNumber(
		EDITOR_NCS_MAX_PARTICIPANTS,
		DEFAULT_MAX_EDIT_PARTICIPANTS,
	);

	const livePagesLimitConfig = StatsigConfigurations.getString(LIVE_PAGES_LIMITS, '');
	const { viewLimit: livePageViewLimit, editLimit: livePageEditLimit } = useMemo(() => {
		const defaultLimits = {
			editLimit: DEFAULT_MAX_EDIT_PARTICIPANTS,
			viewLimit: 100,
		};
		if (typeof livePagesLimitConfig === 'string') {
			try {
				const limitObject = JSON.parse(livePagesLimitConfig) as LivePagesLimitConfig;
				if (!('viewLimit' in limitObject) || !('editLimit' in limitObject)) {
					return defaultLimits;
				}
				return limitObject;
			} catch {
				// ignore and fallback to default
			}
		}
		return defaultLimits;
	}, [livePagesLimitConfig]);

	if (isLivePage) {
		maxEditParticipants = livePageEditLimit;
	}

	const [startHeartbeat] = useExperimentalEditorStartHeartbeatMutation({
		variables: {
			contentId,
			contentType,
			collabService: COLLAB_SERVICE,
			heartbeatUserType: viewMode ? StartUserType.VIEW : StartUserType.EDIT,
		},
	});

	const [stopHeartbeat] = useExperimentalEditorStopHeartbeatMutation({
		variables: {
			contentId,
			contentType,
			heartbeatUserType: viewMode ? StopUserType.VIEW : StopUserType.EDIT,
		},
	});

	const handleError = useCallback(
		(error: Error | CollabHeartbeatError, data?: EditorStartHeartbeatMutationResponse) => {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'error',
					actionSubject: 'heartbeat',
					source: 'useEditorHeartbeat',
					objectId: contentId,
					objectType: contentType,
					attributes: {
						error: {
							reason: error instanceof CollabHeartbeatError ? error.reason : 'Error',
							message: error.message,
						},
						activeUsersSize: data?.experimentalEditorStartHeartbeat?.orderedUsers?.length,
					},
				},
			}).fire();
			setHeartbeatError(error as CollabHeartbeatError);
		},
		[contentId, contentType, createAnalyticsEvent],
	);

	const stopHeartbeatCallback = useCallback(() => {
		if (heartbeatTimerId?.current) {
			clearInterval(heartbeatTimerId.current);
		}
		stopHeartbeat().catch((error) => {
			handleError(error);
		});
	}, [stopHeartbeat, handleError]);

	const handleHeartbeatSubtypeMismatch = useCallback(
		(subtype: string | null | undefined) => {
			setHeartbeatSubtype(subtype);

			// Detect if page type (live/classic page) differs between frontend and backend
			// This can happen when a page is converted but the user hasn't received the event
			// We disable the editor in NCS to prevent potential data loss in this state
			const hasSubtypeMismatch =
				(isLivePage && subtype !== 'live') || // Live doc with a different subtype
				(!isLivePage && subtype !== null); // Non-live doc with any subtype

			setSubtypeMismatch(hasSubtypeMismatch);
		},
		[isLivePage, setHeartbeatSubtype, setSubtypeMismatch],
	);
	const startHeartbeatCallback = useCallback(() => {
		startHeartbeat()
			.then((result) => {
				const { data } = result;
				if (isResettingHeartbeat.current) {
					isResettingHeartbeat.current = false;
				}

				if (!data || !data.experimentalEditorStartHeartbeat) {
					handleError(
						new CollabHeartbeatError(
							'Missing response data',
							!data ? 'No data' : 'No heartbeat data',
						),
					);
					return;
				}

				const { error, message, orderedUsers, editorSessionId, subtype } =
					data!.experimentalEditorStartHeartbeat!;

				editorSessionIdRef.current = editorSessionId;

				if (fg('confluence_ncs_disconnect_idle_tabs_pubsub')) {
					handleHeartbeatSubtypeMismatch(subtype);
				}

				if (error) {
					const heartbeatError = new CollabHeartbeatError(message ?? 'Heartbeat failed', error);

					if (isLivePage && isPermissionError(heartbeatError)) {
						// heartbeat permission check is a redundant extra check on top of NCS permission enforcement
						// for live pages:
						//    there is a race condition where heartbeat is sent with outdated user type
						//    we ignore the permission error and re-send the heartbeat with the correct user type
						return;
					}

					handleError(heartbeatError, data);
				}

				if (!!orderedUsers) {
					setOrderedActiveUsers(orderedUsers);
				}
			})
			.catch((error) => {
				handleError(error);
			});
	}, [startHeartbeat, handleError, isLivePage, handleHeartbeatSubtypeMismatch]);

	useEffect(() => {
		if (!orderedActiveUsers || isResettingHeartbeat.current) return;

		const isCurrentUserOverLimit = ({
			activeUsers,
			limit,
		}: {
			activeUsers: string[];
			limit: number;
		}) => {
			// If there are less active users than the limit, the user is not over the limit
			if (activeUsers.length < limit) return false;

			const isUserAnActiveUser = userId ? activeUsers.slice(0, limit).includes(userId) : false;
			return (
				// heartbeat backend doesn't handle anonymous users, they will be locked out regardless of order that they joined (i.e. we just use activeUsers size number comparison for anonymous users)
				// otherwise, only the first N users should be able to continue editing
				(!userId && activeUsers.length > limit) || (!!userId && !isUserAnActiveUser)
			);
		};

		const editUsers =
			orderedActiveUsers
				.filter((user) => user.heartbeatUserType === StartUserType.EDIT)
				.map((user) => user.accountId) ?? [];

		const isEditLimitExceeded = isCurrentUserOverLimit({
			activeUsers: editUsers,
			limit: maxEditParticipants,
		});

		if (!isLivePage) {
			setCollabLimitReached(isEditLimitExceeded);
			return;
		}

		// include edit->view fallback users, based on queue order
		const allowedEditUsers = editUsers.slice(0, maxEditParticipants);
		const viewUsers =
			orderedActiveUsers
				.filter((user) => !allowedEditUsers.includes(user.accountId))
				.map((user) => user.accountId) ?? [];

		const isViewLimitExceeded = isCurrentUserOverLimit({
			activeUsers: viewUsers,
			limit: livePageViewLimit,
		});

		if (isEditLimitExceeded && !(prevViewModeRef.current || isViewLimitExceeded)) {
			setLiveEditLimitReached(true);
		} else {
			setLiveEditLimitReached(false);
		}

		if (isViewLimitExceeded && (prevViewModeRef.current || isEditLimitExceeded)) {
			setLiveViewLimitReached(true);
		} else {
			setLiveViewLimitReached(false);
		}
	}, [orderedActiveUsers, maxEditParticipants, userId, isLivePage, livePageViewLimit]);

	useEffect(() => {
		if (viewMode !== undefined && viewMode !== prevViewModeRef.current) {
			// view mode has changed, refetch heartbeat with new user type
			isResettingHeartbeat.current = true;
			startHeartbeatCallback();
			prevViewModeRef.current = viewMode;
		}
	}, [viewMode, startHeartbeatCallback]);

	useEffect(() => {
		if (collabEditInitialized) {
			startHeartbeatCallback();

			heartbeatTimerId.current = setInterval(startHeartbeatCallback, getHeartbeatTimerInterval());

			return () => stopHeartbeatCallback();
		}
		// prevent re-triggering start/stop when heartbeat type changes (i.e. when mutation callback changes)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		collabEditInitialized,
		contentId, // only re-trigger heartbeat on specific mutation variables, not viewMode
		contentType,
	]);

	return {
		// regular edit pages
		collabLimitReached,
		// live pages specific
		liveEditLimitReached,
		liveViewLimitReached,
		// shared data
		heartbeatError,
		stopHeartbeatCallback,
		editorSessionId: editorSessionIdRef.current,
		subtypeMismatch,
		heartbeatSubtype,
	};
};
